import type { DocNode } from '@atlaskit/adf-schema/doc';

import { ConfluencePageAri } from '@atlassian/ari';

import {
	extractSmartLinkSourceExcerptsFromAdf,
	SOURCE_EXCERPT_LIMIT,
} from '../utils/extractSmartLinkSourceExcerptsFromAdf.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type {
	AtAGlanceCollection,
	AtAGlanceEntityContext,
	AtAGlanceFeatureInput,
	AtAGlanceLinkedEntity,
	AtAGlanceRelationship,
	AtAGlanceStructuralAncestor,
} from './atAGlanceTypes.ts';
import { extractAdfExcerpt } from './extractAdfExcerpt.ts';

export const TITLE_LIMIT = 500;
export const ENTITY_SUMMARY_LIMIT = 500;
export const ENTITY_STATUS_LIMIT = 200;
export const PRODUCT_LIMIT = 100;
export const RESOURCE_TYPE_LIMIT = 100;
export const LOCALE_LIMIT = 35;

type BuildAtAGlanceInputOptions = {
	cloudId: string;
	contentId: string;
	locale: string;
	pageTitle: string;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	pageAdf: DocNode;
	structuralAncestors?: readonly AtAGlanceStructuralAncestor[] | null;
	incomingLinks: EnhancedSmartLink[];
	outgoingLinks: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
};

const bound = (value: string | undefined, limit: number): string | undefined => {
	const normalized = value?.trim();
	return normalized ? normalized.slice(0, limit) : undefined;
};

const toIsoInstant = (value: string | null | undefined): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}

	const normalized = value.trim();
	if (!normalized || !/(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized)) {
		return undefined;
	}

	const timestamp = new Date(normalized);
	return Number.isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString();
};

const toPositiveContentVersion = (value: number | null | undefined): number | undefined =>
	typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;

type LinkBackedCollection = Exclude<AtAGlanceCollection, 'ancestors'>;

const relationshipFor = (
	collection: LinkBackedCollection,
	link: EnhancedSmartLink,
	sourceExcerpts: Map<string, string>,
): AtAGlanceRelationship => {
	if (collection === 'appears_in') {
		return {
			collection,
			type: 'content_referenced_entity',
			direction: 'incoming',
			source_excerpt: null,
		};
	}
	if (collection === 'related_content') {
		return {
			collection,
			type: 'confluence_content_has_related_confluence_content',
			direction: 'outgoing',
			source_excerpt: null,
		};
	}
	return {
		collection,
		type: 'page_contains_smart_link',
		direction: 'outgoing',
		source_excerpt: bound(sourceExcerpts.get(link.url), SOURCE_EXCERPT_LIMIT) ?? null,
	};
};

const entityContextFor = (link: EnhancedSmartLink): AtAGlanceEntityContext | undefined => {
	const summary = bound(link.summary, ENTITY_SUMMARY_LIMIT);
	const status =
		bound(link.taskStatus, ENTITY_STATUS_LIMIT) ?? bound(link.state, ENTITY_STATUS_LIMIT);
	const updatedAt = toIsoInstant(link.updatedAt);
	return summary || status || updatedAt
		? {
				...(summary ? { summary } : {}),
				...(status ? { status } : {}),
				...(updatedAt ? { updated_at: updatedAt } : {}),
			}
		: undefined;
};

const mergeEntityContext = (
	primary: AtAGlanceEntityContext | undefined,
	secondary: AtAGlanceEntityContext | undefined,
): AtAGlanceEntityContext | undefined => {
	if (!primary) {
		return secondary;
	}
	if (!secondary) {
		return primary;
	}

	const summary = primary.summary ?? secondary.summary;
	const status = primary.status ?? secondary.status;
	const updatedAt = primary.updated_at ?? secondary.updated_at;
	return {
		...(summary ? { summary } : {}),
		...(status ? { status } : {}),
		...(updatedAt ? { updated_at: updatedAt } : {}),
	};
};

const toEntity = (
	link: EnhancedSmartLink,
	collection: LinkBackedCollection,
	sourceExcerpts: Map<string, string>,
): AtAGlanceLinkedEntity | null => {
	const title = bound(link.title, TITLE_LIMIT);
	if (link.access !== 'granted' || !link.ari || !title) {
		return null;
	}

	const entityContext = entityContextFor(link);
	const product = bound(link.product, PRODUCT_LIMIT);
	const resourceType = bound(link.resourceType, RESOURCE_TYPE_LIMIT);

	return {
		ari: link.ari,
		title,
		...(product ? { product } : {}),
		...(resourceType ? { resource_type: resourceType } : {}),
		relationships: [relationshipFor(collection, link, sourceExcerpts)],
		...(entityContext ? { entity_context: entityContext } : {}),
	};
};

const collectEntities = (
	links: EnhancedSmartLink[],
	collection: LinkBackedCollection,
	sourceExcerpts: Map<string, string>,
): AtAGlanceLinkedEntity[] => {
	const entities: AtAGlanceLinkedEntity[] = [];
	for (const link of links) {
		const entity = toEntity(link, collection, sourceExcerpts);
		if (entity) {
			entities.push(entity);
		}
	}
	return entities;
};

const structuralParentEntityFor = (
	cloudId: string,
	ancestors: readonly AtAGlanceStructuralAncestor[] | null | undefined,
): AtAGlanceLinkedEntity | null => {
	if (!ancestors) {
		return null;
	}

	// Confluence ancestors are root-first. Select one nearest page ancestor so
	// hierarchy evidence stays useful and bounded even below folders/databases.
	for (let index = ancestors.length - 1; index >= 0; index--) {
		const ancestor = ancestors[index];
		const id = ancestor?.id?.trim();
		const title = bound(ancestor?.title ?? undefined, TITLE_LIMIT);
		if (!id || !title || ancestor?.type?.toLowerCase() !== 'page') {
			continue;
		}

		return {
			ari: ConfluencePageAri.create({ siteId: cloudId, pageId: id }).toString(),
			title,
			product: 'confluence',
			resource_type: 'page',
			relationships: [
				{
					collection: 'ancestors',
					type: 'confluence_page_has_child_confluence_page',
					direction: 'incoming',
					source_excerpt: null,
				},
			],
		};
	}

	return null;
};

/**
 * Deduplicates entities by ARI and keeps one relationship per collection. An entity reached
 * through several collections carries all of them: each names a different thing the page and the
 * entity have to do with each other, and only the `links_to` relationship can carry a
 * `source_excerpt`. The count needs no limit of its own — `AtAGlanceCollection` has four members,
 * so four relationships is the ceiling.
 */
const mergeByAri = (entities: AtAGlanceLinkedEntity[]): AtAGlanceLinkedEntity[] => {
	const byAri = new Map<string, AtAGlanceLinkedEntity>();

	for (const entity of entities) {
		const existing = byAri.get(entity.ari);
		const nextRelationship = entity.relationships[0];
		if (!existing) {
			byAri.set(entity.ari, entity);
			continue;
		}
		if (!nextRelationship) {
			continue;
		}

		const existingRelationshipIndex = existing.relationships.findIndex(
			(relationship) => relationship.collection === nextRelationship.collection,
		);
		if (existingRelationshipIndex !== -1) {
			const existingRelationship = existing.relationships[existingRelationshipIndex];
			if (!existingRelationship) {
				continue;
			}
			const entityContext = mergeEntityContext(existing.entity_context, entity.entity_context);
			const relationship =
				!existingRelationship.source_excerpt && nextRelationship.source_excerpt
					? { ...existingRelationship, source_excerpt: nextRelationship.source_excerpt }
					: existingRelationship;
			const relationships = [...existing.relationships];
			relationships[existingRelationshipIndex] = relationship;
			byAri.set(entity.ari, {
				...existing,
				product: existing.product ?? entity.product,
				resource_type: existing.resource_type ?? entity.resource_type,
				relationships,
				...(entityContext ? { entity_context: entityContext } : {}),
			});
			continue;
		}

		const entityContext = mergeEntityContext(existing.entity_context, entity.entity_context);
		byAri.set(entity.ari, {
			...existing,
			product: existing.product ?? entity.product,
			resource_type: existing.resource_type ?? entity.resource_type,
			relationships: [...existing.relationships, nextRelationship],
			...(entityContext ? { entity_context: entityContext } : {}),
		});
	}

	return [...byAri.values()];
};

/**
 * Builds the cc-smarts `twg-hub-summaries` request from the modal's connection
 * data and the current page ADF. Returns null when there is nothing useful to
 * send (no page text, or no access-granted linked entities with ARIs).
 */
export const buildAtAGlanceInput = ({
	cloudId,
	contentId,
	locale,
	pageTitle,
	pageVersion,
	pageUpdatedAt,
	pageAdf,
	structuralAncestors,
	incomingLinks,
	outgoingLinks,
	relatedContentLinks = [],
}: BuildAtAGlanceInputOptions): AtAGlanceFeatureInput | null => {
	const normalizedLocale = locale.trim();
	const title = bound(pageTitle, TITLE_LIMIT);
	if (!normalizedLocale || normalizedLocale.length > LOCALE_LIMIT || !title) {
		return null;
	}

	const { excerpt, truncated } = extractAdfExcerpt(pageAdf);
	if (!excerpt) {
		return null;
	}

	const sourceExcerpts =
		outgoingLinks.length > 0
			? extractSmartLinkSourceExcerptsFromAdf(pageAdf)
			: new Map<string, string>();
	const incoming = collectEntities(incomingLinks, 'appears_in', sourceExcerpts);
	const outgoing = collectEntities(outgoingLinks, 'links_to', sourceExcerpts);
	const related = collectEntities(relatedContentLinks, 'related_content', sourceExcerpts);
	const structuralParent = structuralParentEntityFor(cloudId, structuralAncestors);
	// Every deduplicated entity is sent. cc-smarts reconciles this inventory with its own
	// unbounded graph discovery, and it cannot recover what is dropped here: incoming-only
	// entities, related content, ancestors, and outgoing links whose graph type its discovery
	// does not decode all reach the model only via this list.
	//
	// The order is evidence strength, not priority for a cap: related content (the only
	// collection whose one-liners the UI renders), then the structural parent, then links the
	// page makes explicitly, then references to it. `mergeByAri` keeps an entity at its first
	// occurrence, so the order is deterministic and the request body stays a stable cache key.
	const linkedEntities = mergeByAri([
		...related,
		...(structuralParent ? [structuralParent] : []),
		...outgoing,
		...incoming,
	]);
	if (linkedEntities.length === 0) {
		return null;
	}

	const version = toPositiveContentVersion(pageVersion);
	const updatedAt = toIsoInstant(pageUpdatedAt);

	return {
		content_ari: ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString(),
		locale: normalizedLocale,
		central_page: {
			title,
			mime_type: 'text/markdown',
			excerpt,
			truncated,
			...(version ? { version } : {}),
			...(updatedAt ? { updated_at: updatedAt } : {}),
		},
		linked_entities: linkedEntities,
	};
};
